// @file For linkings on tldraw card
import { trackEvent } from '@@/bits/analytics'
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { validateExternalUrl } from '@@/bits/url'
import { deleteShapeMetadata } from '@@/bits/whiteboard'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import type { Frame } from '@@/types/whiteboard'
import { handleLinkClick } from '@@/vuecomposables/process_post_or_comment_body'
import { flattenDeep } from 'es-toolkit'
import type { Editor, HistoryEntry, TLPageId, TLRecord, TLShape, TLShapeId } from 'tldraw'
import { react, uniq } from 'tldraw'

export type WhiteboardLinkingLinkTargetType =
  | 'url'
  | 'frame'
  | 'frameStep'
  | 'padletHashId'
  | 'positionalFrame'
  | 'relativeFrame'

export enum WhiteboardPositionalFrameTarget {
  First = 'first',
  Last = 'last',
}

export enum WhiteboardRelativeFrameTarget {
  LastViewed = 'lastViewed',
}

export enum LinkError {
  InvalidUrl = 'Invalid URL',
}

class WhiteboardLinkingLink {
  targetType: WhiteboardLinkingLinkTargetType
  target: string
  linkError: LinkError | undefined

  constructor(targetType: WhiteboardLinkingLinkTargetType, target: string) {
    this.targetType = targetType
    this.target = target
    if (targetType === 'url') {
      const isValidUrl = validateExternalUrl(target)
      this.linkError = isValidUrl ? undefined : LinkError.InvalidUrl
    } else {
      this.linkError = undefined
    }
  }

  static fromString(str: string): WhiteboardLinkingLink {
    const [targetType, target] = str.split('->')
    return new WhiteboardLinkingLink(targetType as WhiteboardLinkingLinkTargetType, target)
  }

  toString(): string {
    return `${this.targetType}->${this.target}`
  }

  toReadableString(frames: Frame[]): string {
    switch (this.targetType) {
      case 'url':
        return this.target
      case 'frame': {
        const frameNumber = frames.findIndex((frame) => frame.id === this.target) + 1
        if (frameNumber === 0) return __('Card not found')
        return __('Card %{cardNumber}', { cardNumber: frameNumber })
      }
      case 'frameStep': {
        const steps = parseInt(this.target, 10)
        if (steps === 1) {
          return __('Next card')
        }
        if (steps === -1) {
          return __('Previous card')
        }
        if (steps > 1) {
          return __('%{steps} cards ahead', { steps })
        }
        return __('%{steps} cards behind', { steps: Math.abs })
      }
      case 'positionalFrame': {
        const specifiedFrame = this.target as WhiteboardPositionalFrameTarget
        if (specifiedFrame === WhiteboardPositionalFrameTarget.First) {
          return __('First card')
        }
        return __('Last card')
      }
      case 'relativeFrame': {
        return __('Last viewed card')
      }
      case 'padletHashId':
        throw new Error('Not implemented')
    }
  }

  errorMessage(editor: Editor): string {
    switch (this.targetType) {
      case 'url':
        return this.linkError === LinkError.InvalidUrl ? __('Invalid URL') : ''
      case 'frame':
        return editor.getPages().some((page) => page.id === this.target) ? '' : __('Card not found')
      case 'frameStep': {
        const stepNumber = parseInt(this.target, 10)
        const pages = editor.getPages()
        const currentPageIndex = pages.findIndex((page) => page.id === editor.getCurrentPageId())
        if (stepNumber === -1 && currentPageIndex === 0) {
          return __('Already on first card')
        }
        if (stepNumber === 1 && currentPageIndex === pages.length - 1) {
          return __('Already on last card')
        }
        if (currentPageIndex + stepNumber < 0 || currentPageIndex + stepNumber >= pages.length) {
          return __('Invalid navigation')
        }
        return ''
      }
      case 'positionalFrame': {
        const positionalFrameTarget = this.target as WhiteboardPositionalFrameTarget
        const pages = editor.getPages()
        const currentPageId = editor.getCurrentPageId()
        const currentPageIndex = pages.findIndex((page) => page.id === currentPageId)

        if (positionalFrameTarget === WhiteboardPositionalFrameTarget.First && currentPageIndex === 0) {
          return __('Already on first card')
        }

        if (positionalFrameTarget === WhiteboardPositionalFrameTarget.Last && currentPageIndex === pages.length - 1) {
          return __('Already on last card')
        }
        return ''
      }
      case 'relativeFrame': {
        const relativeFrameTarget = this.target as WhiteboardRelativeFrameTarget
        const lastSeenPageId = useWhiteboardStore().previousFrameId
        const currentPageId = editor.getCurrentPageId()
        if (relativeFrameTarget === WhiteboardRelativeFrameTarget.LastViewed && lastSeenPageId === currentPageId) {
          return __('Already on last viewed card')
        }
        return ''
      }
      case 'padletHashId':
        throw new Error('Not implemented')
    }
  }

  handle(editor: Editor): void {
    if (useWhiteboardStore().isFrameBreakout) {
      // We don't want users to be able to navigate frames in breakout mode
      return
    }

    switch (this.targetType) {
      case 'url':
        if (this.linkError === LinkError.InvalidUrl) {
          // Don't navigate if link is invalid -- XSS risk
          // See https://linear.app/padlet/issue/ENG-19286/finding-lt-app24-41-stored-cross-site-scripting-in-link-attachment
          return
        }
        void handleLinkClick(this.target)
        break
      case 'frame':
        editor.setCurrentPage(this.target as TLPageId)
        break
      case 'frameStep': {
        const stepNumber = parseInt(this.target, 10)
        const pages = editor.getPages()
        const currentPageIndex = pages.findIndex((page) => page.id === editor.getCurrentPageId())
        const nextPageIndex = currentPageIndex + stepNumber
        if (nextPageIndex < 0 || nextPageIndex >= pages.length) return
        const nextPage = pages[nextPageIndex]
        editor.setCurrentPage(nextPage.id)
        break
      }
      case 'positionalFrame': {
        const positionalFrameTarget = this.target as WhiteboardPositionalFrameTarget
        if (positionalFrameTarget === WhiteboardPositionalFrameTarget.First) {
          const firstPageId = editor.getPages()[0].id
          editor.setCurrentPage(firstPageId)
          return
        }
        if (positionalFrameTarget === WhiteboardPositionalFrameTarget.Last) {
          const lastPageId = editor.getPages()[editor.getPages().length - 1].id
          editor.setCurrentPage(lastPageId)
        }
        break
      }
      case 'relativeFrame': {
        const relativeFrameTarget = this.target as WhiteboardRelativeFrameTarget
        if (relativeFrameTarget === WhiteboardRelativeFrameTarget.LastViewed) {
          const lastSeenPageId = useWhiteboardStore().previousFrameId
          if (lastSeenPageId != null) editor.setCurrentPage(lastSeenPageId)
        }
        break
      }
      case 'padletHashId':
        throw new Error('Not implemented')
    }
  }
}

function upsertLinkingToAllShapesInGroup(editor: Editor, groupId: TLShapeId, linking: WhiteboardLinkingLink): void {
  const children = editor.getCurrentPageShapes().filter((shape) => shape.parentId === groupId)
  children.forEach((shape) => {
    editor.updateShape({
      id: shape.id,
      type: shape.type,
      meta: {
        linking: linking.toString(),
      },
    })
  })
}

function upsertLinkingToShape(
  editor: Editor,
  shapeId: TLShapeId,
  targetType: WhiteboardLinkingLinkTargetType,
  target: string,
): TLShape | undefined {
  const shape = editor.getShape(shapeId)
  if (shape == null) return
  const linking = new WhiteboardLinkingLink(targetType, target)
  editor.updateShape({
    id: shape.id,
    type: shape.type,
    meta: {
      linking: linking.toString(),
    },
  })
  if (editor.isShapeOfType(shape, 'group')) {
    // If a group of shapes with a link is ungrouped, each shape will retain the link
    upsertLinkingToAllShapesInGroup(editor, shapeId, linking)
  }
  return editor.getShape(shapeId)
}

function getLinkingFromShape(shape: TLShape): WhiteboardLinkingLink | undefined {
  if (shape.meta.linking == null) return
  if (typeof shape.meta.linking !== 'string') return
  return WhiteboardLinkingLink.fromString(shape.meta.linking)
}

function removeLinkingFromShape(editor: Editor, shapeId: TLShapeId): TLShape | undefined {
  const shape = editor.getShape(shapeId)
  if (shape == null) return
  deleteShapeMetadata(shape.id, ['linking'])
  return editor.getShape(shapeId)
}

function initializeLinking(editor: Editor): void {
  react('get links on page changes', () => {
    const shapesOnPage = editor.getCurrentPageShapes()
    shapesOnPage.forEach((shape) => {
      const linking = getLinkingFromShape(shape)
      if (linking == null) return
      void useWhiteboardLinkingStore().upsertLinking(shape.id, linking)
    })
  })
}

function listenToLinkingChanges(editor: Editor): void {
  editor.store.listen(
    (event: HistoryEntry<TLRecord>) => {
      const changes = uniq(
        flattenDeep([
          Object.values(event.changes.added),
          Object.values(event.changes.updated),
          Object.values(event.changes.removed),
        ]),
      )
      changes.forEach((change) => {
        const shape = change as TLShape
        if (shape.meta.linking == null || typeof shape.meta.linking !== 'string' || shape.meta.linking === '') {
          void useWhiteboardLinkingStore().deleteLinking(shape.id)
          return
        }
        void useWhiteboardLinkingStore().upsertLinking(shape.id, WhiteboardLinkingLink.fromString(shape.meta.linking))
      })
    },
    { scope: 'document', source: 'all' },
  )
}

function triggerLinking(editor: Editor, linking: WhiteboardLinkingLink): void {
  if (linking == null) {
    return
  }
  linking.handle(editor)
  trackEvent('Whiteboard', 'Clicked shape link', linking.targetType, null, { link: linking.toString() })
}

/**
 * Handle linking behavior when grouping multiple shapes:
 * 1. If only one shape has a link, use that link as the new group link.
 * 2. If multiple shapes have links, remove all links and show a toast with an undo option.
 * 3. If no shapes have links, do nothing.
 * @param {TLShape[]} shapes - The shapes being grouped
 * @param {string} markId - The mark id to bail to if the user undoes the grouping
 */
function handleGroupLinking(shapes: TLShape[], markId: string): void {
  const editor = getEditorObject()
  if (editor == null) return

  const shapesWithLinking = shapes.filter((shape) => getLinkingFromShape(shape)?.target != null)
  const linkingCount = shapesWithLinking.length
  if (linkingCount === 0) return

  const firstShapeLinking = getLinkingFromShape(shapesWithLinking[0])
  if (firstShapeLinking == null) return
  const allShapesLinkToTheSameTarget = shapesWithLinking.every((shape) => {
    const linking = getLinkingFromShape(shape)
    return linking != null && linking.target === firstShapeLinking?.target
  })

  if (linkingCount === 1 || allShapesLinkToTheSameTarget) {
    useWhiteboardLinkingStore().setActiveGroupLinking(firstShapeLinking)
  } else if (linkingCount > 1) {
    useWhiteboardLinkingStore().setActiveGroupLinking(null)
    shapes.forEach((shape) => removeLinkingFromShape(editor, shape.id))
    useGlobalSnackbarStore().setSnackbar({
      message: __('Links have been removed from objects in this group'),
      actionText: __('Undo linking'),
      actionTextActions: [() => editor.bailToMark(markId)],
    })
  }
}

export {
  getLinkingFromShape,
  handleGroupLinking,
  initializeLinking,
  listenToLinkingChanges,
  removeLinkingFromShape,
  triggerLinking,
  upsertLinkingToAllShapesInGroup,
  upsertLinkingToShape,
  WhiteboardLinkingLink,
}
